// Main Terminal component
import React, { useState, useEffect, useRef, useCallback } from 'react';
import CommandMenu from './CommandMenu';
import TerminalOutput from './TerminalOutput';
import TerminalInput from './TerminalInput';
import { commands } from '../../data/commands';
import { caseStudies, caseStudyList } from '../../data/caseStudies';
import { STEP_MS } from './ToolTrace';
import { THEMES } from '../../hooks/useTheme';

// figlet, "slant". Kept as separate strings so source indentation
// can never corrupt the alignment.
const BANNER = [
  '    ___  ______________  ____  ____',
  '   /   |/_  __/_  __/ / / / / / / /',
  '  / /| | / /   / / / / / / / / / /',
  ' / ___ |/ /   / / / /_/ / /_/ / /___',
  '/_/  |_/_/   /_/  \\____/\\____/_____/'
];

const WELCOME = [
  "Hi, I am ATTUUL GERIYAN, an Applied AI Lead.",
  'Building AI-powered engineering workflows & cloud-native backends at scale.',
  'Welcome to my interactive portfolio terminal!',
  'Type "help" to see available commands, or press ⌘K to search.',
  ''
];

// Traces for the ordinary commands, so every command reads like the
// agent actually went and looked something up.
const COMMAND_TRACES = {
  about: [{ tool: 'Read', args: 'resume.pdf --section summary', ms: 11 }],
  skills: [{ tool: 'Read', args: 'resume.pdf --section skills', ms: 9 }],
  experience: [
    { tool: 'Read', args: 'resume.pdf --section experience', ms: 12 },
    { tool: 'Sort', args: 'roles --by start_date desc', ms: 2, result: '5 roles · 2022 → present' }
  ],
  education: [{ tool: 'Read', args: 'resume.pdf --section education', ms: 8 }],
  certifications: [{ tool: 'Read', args: 'resume.pdf --section achievements', ms: 8 }],
  contact: [{ tool: 'Read', args: 'contact.json', ms: 6 }]
};

// Commands that take an argument, and what completes for each. Derived
// from the data so a new case study or theme is completable for free.
const ARG_COMPLETIONS = {
  'case-study': caseStudyList,
  theme: THEMES,
  projects: [],
  sudo: ['please']
};

let entryId = 0;
const nextId = () => { entryId += 1; return entryId; };

const Terminal = ({ theme, setTheme, github }) => {
  const [history, setHistory] = useState([
    { id: nextId(), type: 'banner', content: BANNER },
    { id: nextId(), type: 'output', content: WELCOME }
  ]);
  const [currentInput, setCurrentInput] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);
  const [lastCommand, setLastCommand] = useState(null);
  const terminalRef = useRef(null);
  const inputRef = useRef(null);
  const cancelRef = useRef(false);
  // Guards re-entry synchronously: `isProcessing` only updates on the
  // next render, so two events in one tick would both slip past it.
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const clearedRef = useRef(0);

  // Set on mount as well as cleared on unmount: StrictMode's
  // mount/unmount/remount would otherwise leave this false forever.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const addToHistory = useCallback((entry) => {
    setHistory((prev) => [...prev, { id: nextId(), ...entry }]);
  }, []);

  const typeWriter = useCallback(async (text, speed = 10) => {
    const id = nextId();
    const epoch = clearedRef.current;
    setHistory((prev) => [...prev, { id, type: 'typing', content: '' }]);

    for (let i = 0; i <= text.length; i += 1) {
      // `clearedAt` advances on every clear, so a command started
      // before one stops instead of typing into a cleared screen.
      if (cancelRef.current || !mountedRef.current || clearedRef.current !== epoch) break;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, speed));
      const slice = text.slice(0, i);
      setHistory((prev) => prev.map((e) => (e.id === id ? { ...e, content: slice } : e)));
    }

    if (!mountedRef.current || clearedRef.current !== epoch) return;

    setHistory((prev) => prev.map((e) => (
      e.id === id ? { ...e, type: 'output', content: cancelRef.current ? `${e.content}^C` : text } : e
    )));
  }, []);

  const printLines = useCallback(async (lines) => {
    for (const line of lines) {
      if (cancelRef.current) break;
      // eslint-disable-next-line no-await-in-loop
      await typeWriter(line, 6);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 30));
    }
  }, [typeWriter]);

  // Resolves when the trace itself reports finished, so the output can
  // never land while a step is still spinning.
  const runTrace = useCallback((steps) => new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      setTimeout(resolve, 120);
    };
    addToHistory({ type: 'trace', steps, onDone: done });
    // Backstop: if the entry is cleared before it finishes, don't hang.
    setTimeout(done, steps.length * STEP_MS + 1500);
  }), [addToHistory]);

  const triggerMatrixEffect = () => {
    setShowMatrix(true);
    setTimeout(() => setShowMatrix(false), 3000);
  };

  const downloadResume = () => {
    const link = document.createElement('a');
    link.href = '/Attuul_Geriyan_Resume.pdf';
    link.download = 'Attuul_Geriyan_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const executeCommand = useCallback(async (cmd) => {
    const trimmedCmd = cmd.trim();
    const lowerCmd = trimmedCmd.toLowerCase();
    const [verb, ...rest] = lowerCmd.split(/\s+/);
    const arg = rest.join(' ');

    busyRef.current = true;
    setIsProcessing(true);
    cancelRef.current = false;
    if (verb) setLastCommand(verb === 'casestudy' ? 'case-study' : verb);

    addToHistory({ type: 'command', content: trimmedCmd });

    try {
      if (!trimmedCmd) return;

      if (lowerCmd === 'clear') {
        clearedRef.current += 1;
        setHistory([]);
        return;
      }

      if (lowerCmd === 'sudo rm -rf /' || lowerCmd === 'sudo rm -rf /*') {
        await typeWriter('Permission denied. Nice try! 😄');
        triggerMatrixEffect();
        return;
      }

      if (verb === 'sudo') {
        const easterEggs = [
          'sudo: you are not in the sudoers file. This incident will be reported.',
          'Nice try! But I am the admin here 😎',
          'Access denied! Try "sudo please" next time 🤖',
          'Error 418: I am a teapot, not a server!',
          'sudo: command not found. Did you mean "please"?',
          'With great power comes great responsibility... denied!',
          'sudo make me a sandwich... Error: You are not a sandwich maker! 🥪',
          'Attempting to hack the matrix... Access denied! 🕶️'
        ];
        if (lowerCmd === 'sudo please') {
          await typeWriter('That is better! Here is a cookie 🍪');
          triggerMatrixEffect();
          return;
        }
        await typeWriter(easterEggs[Math.floor(Math.random() * easterEggs.length)]);
        return;
      }

      if (verb === 'theme') {
        if (!arg) {
          await printLines([
            '🎨 THEMES',
            '',
            ...THEMES.map((t) => `   ${t === theme ? '●' : '○'} ${t}${t === 'matrix' ? '   (the original look)' : ''}`),
            '',
            'Usage: theme <name>',
            ''
          ]);
          return;
        }
        if (setTheme(arg)) {
          await typeWriter(`Theme set to ${arg}.`);
        } else {
          await typeWriter(`Unknown theme: ${arg}. Try one of: ${THEMES.join(', ')}`);
        }
        return;
      }

      if (verb === 'case-study' || verb === 'casestudy') {
        if (!arg || !caseStudies[arg]) {
          await printLines([
            '📊 CASE STUDIES',
            '',
            ...caseStudyList.map((slug) => `   case-study ${slug}${' '.repeat(Math.max(1, 14 - slug.length))}${caseStudies[slug].title}`),
            '',
            ...(arg ? [`Unknown case study: ${arg}`, ''] : [])
          ]);
          return;
        }
        const study = caseStudies[arg];
        await runTrace(study.trace);
        addToHistory({ type: 'case-study', study });
        return;
      }

      if (verb === 'projects' || lowerCmd === 'ls ~/projects') {
        if (github.status === 'error') github.retry();
        await runTrace([
          { tool: 'Fetch', args: 'api.github.com/users/attuulGeriyan/repos', ms: 210 }
        ]);
        addToHistory({ type: 'projects' });
        return;
      }

      if (verb === 'resume') {
        await printLines(commands.resume);
        downloadResume();
        return;
      }

      const command = commands[lowerCmd];
      if (command) {
        if (COMMAND_TRACES[lowerCmd]) {
          await runTrace(COMMAND_TRACES[lowerCmd]);
        }
        await printLines(command);
        return;
      }

      await typeWriter(`Command not found: ${lowerCmd}. Type 'help' for available commands.`);
    } finally {
      cancelRef.current = false;
      busyRef.current = false;
      if (mountedRef.current) setIsProcessing(false);
    }
  }, [addToHistory, typeWriter, printLines, runTrace, theme, setTheme, github]);

  const handleSubmit = useCallback(async (input) => {
    // `clear` interrupts rather than queues: it must work mid-command,
    // from the chip and the palette as well as the keyboard.
    if (input.trim().toLowerCase() === 'clear') {
      cancelRef.current = true;
      clearedRef.current += 1;
      setHistory([]);
      setCurrentInput('');
      setHistoryIndex(-1);
      inputRef.current?.focus();
      return;
    }

    if (busyRef.current) return;
    if (input.trim()) {
      setCommandHistory((prev) => [input, ...prev]);
    }
    setHistoryIndex(-1);
    setCurrentInput('');
    await executeCommand(input);
  }, [executeCommand]);

  // The palette and menu both dispatch through here.
  useEffect(() => {
    const handler = (e) => handleSubmit(e.detail);
    window.addEventListener('portfolio:run', handler);
    return () => window.removeEventListener('portfolio:run', handler);
  }, [handleSubmit]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'c' && e.ctrlKey && !window.getSelection()?.toString()) {
        cancelRef.current = true;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // The prompt is an invisible input, so it has to hold focus for
  // typing to work at all: focus it on load, and take it back when a
  // command finishes or the user starts typing anywhere on the page.
  useEffect(() => {
    if (!isProcessing) inputRef.current?.focus();
  }, [isProcessing]);

  useEffect(() => {
    const onWindowKeyDown = (e) => {
      const el = inputRef.current;
      if (!el || document.activeElement === el) return;
      // Leave real controls, the palette and modifier shortcuts alone.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target.closest('input, textarea, button, a, [role="dialog"]')) return;
      if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Enter') {
        el.focus();
      }
    };
    window.addEventListener('keydown', onWindowKeyDown);
    return () => window.removeEventListener('keydown', onWindowKeyDown);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'l' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      cancelRef.current = true;
      clearedRef.current += 1;
      setHistory([]);
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      const raw = currentInput.toLowerCase();
      if (!raw.trim()) return;

      // Completing an argument (`case-study as`) rather than the
      // command itself — the space decides which.
      const parts = raw.split(/\s+/);
      const completingArg = parts.length > 1 || /\s$/.test(raw);
      const head = parts[0];
      const stub = completingArg ? (parts[1] || '') : head;

      const candidates = completingArg
        ? (ARG_COMPLETIONS[head] || [])
        : [...new Set([...Object.keys(commands), ...Object.keys(ARG_COMPLETIONS)])];

      const matches = candidates.filter((c) => c.startsWith(stub));

      if (matches.length === 1) {
        setCurrentInput(completingArg ? `${head} ${matches[0]}` : matches[0]);
      } else if (matches.length > 1) {
        // Complete as far as every match agrees, then list the rest.
        const common = matches.reduce((acc, m) => {
          let i = 0;
          while (i < acc.length && i < m.length && acc[i] === m[i]) i += 1;
          return acc.slice(0, i);
        });
        if (common.length > stub.length) {
          setCurrentInput(completingArg ? `${head} ${common}` : common);
        }
        addToHistory({ type: 'output', content: [matches.join('   ')] });
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyIndex < commandHistory.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setCurrentInput(commandHistory[newIndex] || '');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setCurrentInput(commandHistory[newIndex] || '');
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setCurrentInput('');
      }
    }
  };

  return (
    <div className="terminal-container">
      <CommandMenu onCommandClick={handleSubmit} activeCommand={lastCommand} />

      <div
        ref={terminalRef}
        className="terminal-content"
        onMouseUp={() => {
          // Don't steal focus mid-selection, or the highlight vanishes.
          if (window.getSelection()?.toString()) return;
          inputRef.current?.focus();
        }}
      >
        <TerminalOutput history={history} github={github} />

        {/* Always mounted: unmounting it destroys focus, selection and
            the paste target, and drops every keystroke in between. */}
        <TerminalInput
          ref={inputRef}
          value={currentInput}
          onChange={setCurrentInput}
          onSubmit={handleSubmit}
          onKeyDown={handleKeyDown}
          busy={isProcessing}
        />
      </div>

      {showMatrix && (
        <div className="matrix-rain">
          {Array.from({ length: 30 }).map((_, i) => (
            <div
              key={i}
              className="matrix-column"
              style={{ left: `${(i / 30) * 100}%`, animationDelay: `${Math.random() * 2}s` }}
            >
              {Array.from({ length: 20 })
                .map(() => String.fromCharCode(0x30a0 + Math.random() * 96))
                .join('')}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Terminal;
